/**
 * InputController
 *
 * Per-frame: tells the scene which direction (if any) the player wants to
 * move *this frame*. Handles the input cadence so holding a key/finger doesn't
 * fire at OS keyboard-repeat speed (which varies per platform).
 *
 * Sources:
 *   - Arrow keys + WASD (keyboard) — always active
 *   - Touch / mouse, in one of two modes:
 *       'swipe'    (default) — direction comes from the vector between where
 *                  the finger went down and where it is now. Reference point
 *                  is the touch-down location, NOT screen center, so the
 *                  player's thumb can rest anywhere — including near a screen
 *                  edge — without it being awkward. Supports quick flicks
 *                  (one step) and swipe-and-hold (continuous movement).
 *       'quadrant' — direction comes from where the finger is relative to
 *                  screen center. The original scheme, kept as an option.
 *
 * Cadence (applies to held input in any source):
 *   - New direction (or first press)  -> immediate move
 *   - Same direction held              -> wait INITIAL_DELAY, then repeat every REPEAT
 *
 * Invalid moves (wall / edge) are decided by the caller — this class does not
 * know about the playfield. If the caller ignores a returned direction, the
 * cadence keeps running normally; the player can hold against a wall and then
 * release without weird timer state.
 */

import Phaser from 'phaser';
import type { Direction } from '../scroll/ScrollController';

const INPUT_INITIAL_DELAY_MS = 180;
const INPUT_REPEAT_MS = 110;

/** Quadrant mode: pixels from screen center before a direction registers. */
const QUADRANT_DEAD_ZONE = 10;

/**
 * Swipe mode: minimum distance (px) from the touch-down point before a
 * direction registers. Small enough to feel responsive, large enough that a
 * tap-in-place or tiny jitter doesn't trigger an unwanted move.
 */
const SWIPE_THRESHOLD = 24;

export type TouchMode = 'swipe' | 'quadrant';

type Keys = {
  up: Phaser.Input.Keyboard.Key;
  down: Phaser.Input.Keyboard.Key;
  left: Phaser.Input.Keyboard.Key;
  right: Phaser.Input.Keyboard.Key;
  w: Phaser.Input.Keyboard.Key;
  a: Phaser.Input.Keyboard.Key;
  s: Phaser.Input.Keyboard.Key;
  d: Phaser.Input.Keyboard.Key;
};

export class InputController {
  private scene: Phaser.Scene;
  private screenW: number;
  private screenH: number;
  private keys: Keys;
  private touchMode: TouchMode;

  private currentDir: Direction | null = null;
  private holdStartedAt = 0;
  private lastMoveAt = 0;

  // Swipe tracking: where the active pointer first went down.
  private pointerDownX = 0;
  private pointerDownY = 0;
  private pointerWasDown = false;

  constructor(
    scene: Phaser.Scene,
    screenW: number,
    screenH: number,
    touchMode: TouchMode = 'swipe'
  ) {
    this.scene = scene;
    this.screenW = screenW;
    this.screenH = screenH;
    this.touchMode = touchMode;

    const kb = scene.input.keyboard;
    if (!kb) {
      throw new Error('InputController requires a keyboard plugin');
    }
    const K = Phaser.Input.Keyboard.KeyCodes;
    this.keys = kb.addKeys({
      up: K.UP,
      down: K.DOWN,
      left: K.LEFT,
      right: K.RIGHT,
      w: K.W,
      a: K.A,
      s: K.S,
      d: K.D,
    }) as Keys;
  }

  /** Switch touch scheme at runtime (from a settings toggle). */
  setTouchMode(mode: TouchMode): void {
    this.touchMode = mode;
    // Reset any in-progress gesture so the change takes effect cleanly.
    this.pointerWasDown = false;
    this.currentDir = null;
  }

  getTouchMode(): TouchMode {
    return this.touchMode;
  }

  /**
   * Returns the direction to move this frame, or null.
   * Call exactly once per frame with the current scene time (ms).
   */
  consume(nowMs: number): Direction | null {
    const desired = this.currentDesired();

    if (desired === null) {
      this.currentDir = null;
      return null;
    }

    if (desired !== this.currentDir) {
      // New direction (or first press) — immediate move + reset cadence.
      this.currentDir = desired;
      this.holdStartedAt = nowMs;
      this.lastMoveAt = nowMs;
      return desired;
    }

    // Same direction held — apply cadence.
    if (nowMs - this.holdStartedAt < INPUT_INITIAL_DELAY_MS) return null;
    if (nowMs - this.lastMoveAt >= INPUT_REPEAT_MS) {
      this.lastMoveAt = nowMs;
      return desired;
    }
    return null;
  }

  private currentDesired(): Direction | null {
    // Pointer first — if a finger is down, that's the active input.
    const ptr = this.scene.input.activePointer;
    if (ptr.isDown) {
      // Record the down-point on the transition from up->down.
      if (!this.pointerWasDown) {
        this.pointerWasDown = true;
        this.pointerDownX = ptr.x;
        this.pointerDownY = ptr.y;
      }
      const dir =
        this.touchMode === 'swipe'
          ? this.directionFromSwipe(ptr.x, ptr.y)
          : this.directionFromQuadrant(ptr.x, ptr.y);
      if (dir !== null) return dir;
      // A finger is down but hasn't formed a vector/quadrant yet: no move.
      // (Don't fall through to keyboard — the finger is the active input.)
      return null;
    }

    this.pointerWasDown = false;

    // Keyboard fallback — priority order up > down > left > right.
    const k = this.keys;
    if (k.up.isDown || k.w.isDown) return 'up';
    if (k.down.isDown || k.s.isDown) return 'down';
    if (k.left.isDown || k.a.isDown) return 'left';
    if (k.right.isDown || k.d.isDown) return 'right';
    return null;
  }

  /**
   * Swipe mode: direction is the dominant axis of the vector from the
   * touch-down point to the current pointer position, once it exceeds
   * SWIPE_THRESHOLD. Because the reference is the down-point (wherever the
   * thumb landed), the player never has to reach toward a screen edge.
   *
   * Holding past the threshold keeps returning the same direction, so the
   * cadence system produces continuous movement (swipe-and-hold to glide).
   * Dragging across to a new dominant axis changes direction live.
   */
  private directionFromSwipe(px: number, py: number): Direction | null {
    const dx = px - this.pointerDownX;
    const dy = py - this.pointerDownY;
    if (Math.abs(dx) < SWIPE_THRESHOLD && Math.abs(dy) < SWIPE_THRESHOLD) {
      return null;
    }
    if (Math.abs(dy) > Math.abs(dx)) {
      return dy < 0 ? 'up' : 'down';
    }
    return dx < 0 ? 'left' : 'right';
  }

  /** Quadrant mode: direction relative to screen center (original scheme). */
  private directionFromQuadrant(px: number, py: number): Direction | null {
    const dx = px - this.screenW / 2;
    const dy = py - this.screenH / 2;
    if (Math.abs(dx) < QUADRANT_DEAD_ZONE && Math.abs(dy) < QUADRANT_DEAD_ZONE) {
      return null;
    }
    if (Math.abs(dy) > Math.abs(dx)) {
      return dy < 0 ? 'up' : 'down';
    }
    return dx < 0 ? 'left' : 'right';
  }
}
